/**
 * Customer credit: what is owed, when it falls due, and what has settled it.
 *
 * The governing distinction runs through every name here. An *obligation* is what the customer
 * owes for goods; a *collection* is money that has actually moved. Returning goods reduces the
 * first without touching the second, and refunding money does the reverse. Conflating them is how
 * a shop ends up handing back cash it never took.
 */

/** A plan is live, finished, or withdrawn. Writing off a bad debt is a later phase. */
export const PaymentPlanStatus = {
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;
export type PaymentPlanStatus = (typeof PaymentPlanStatus)[keyof typeof PaymentPlanStatus];

/**
 * What an instalment looks like right now.
 *
 * Only `CANCELLED` is stored. The rest are computed from the amount, what has been allocated to
 * it, and the tenant's local date — so none of them can drift away from the rows underneath.
 */
export const InstallmentState = {
  UPCOMING: 'UPCOMING',
  DUE: 'DUE',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  OVERDUE: 'OVERDUE',
  PAID: 'PAID',
  CANCELLED: 'CANCELLED',
} as const;
export type InstallmentState = (typeof InstallmentState)[keyof typeof InstallmentState];

export const CreditLimitPolicy = {
  /** Refuse new exposure beyond the limit. */
  BLOCK: 'BLOCK',
  /** Allow it and hand back a warning for the counter to see. */
  WARN: 'WARN',
  /** Allow it only for a caller holding `sales.credit_override`, with a recorded reason. */
  ALLOW_WITH_OVERRIDE: 'ALLOW_WITH_OVERRIDE',
} as const;
export type CreditLimitPolicy = (typeof CreditLimitPolicy)[keyof typeof CreditLimitPolicy];

/**
 * When a credit sale gets its invoice.
 *
 * Separate from `invoiceIssuePolicy` on purpose: a tenant may reasonably wait for full payment on
 * a cash sale, and that setting must not silently decide fiscal behaviour for a sale whose whole
 * point is that the customer has not paid. `ON_FULL_PAYMENT` is deliberately absent.
 */
export const CreditInvoiceIssuePolicy = {
  ON_CONFIRMATION: 'ON_CONFIRMATION',
  ON_FIRST_DELIVERY: 'ON_FIRST_DELIVERY',
  MANUAL: 'MANUAL',
} as const;
export type CreditInvoiceIssuePolicy =
  (typeof CreditInvoiceIssuePolicy)[keyof typeof CreditInvoiceIssuePolicy];

export const CreditNoteStatus = { ISSUED: 'ISSUED', VOID: 'VOID' } as const;
export type CreditNoteStatus = (typeof CreditNoteStatus)[keyof typeof CreditNoteStatus];

/** Why a credit note exists, stated when it is issued. A return note names its return; a cancellation note offsets an order's issued invoice and names no return. */
export const CreditNoteKind = {
  RETURN: 'RETURN',
  ORDER_CANCELLATION: 'ORDER_CANCELLATION',
} as const;
export type CreditNoteKind = (typeof CreditNoteKind)[keyof typeof CreditNoteKind];

/** Which document settled an instalment. Exactly one, enforced by a database constraint. */
export const ReceivableAllocationSource = {
  PAYMENT: 'PAYMENT',
  CREDIT_NOTE: 'CREDIT_NOTE',
} as const;
export type ReceivableAllocationSource =
  (typeof ReceivableAllocationSource)[keyof typeof ReceivableAllocationSource];

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

export interface InstallmentSummary {
  id: string;
  sequence: number;
  amountMinor: number;
  /** Local business date, `YYYY-MM-DD`, in the tenant's timezone. */
  dueDate: string;
  allocatedMinor: number;
  outstandingMinor: number;
  state: InstallmentState;
  /** Negative before the due date, positive after. Null once settled or cancelled. */
  daysOverdue: number | null;
}

export interface PaymentPlanSummary {
  id: string;
  reference: string;
  orderId: string;
  orderReference: string;
  branchId: string;
  branchName: string;
  customerId: string;
  customerName: string;
  currencyCode: string;
  /** What was left to owe when the plan was written. Never includes the down payment. */
  totalFinancedMinor: number;
  allocatedMinor: number;
  outstandingMinor: number;
  installmentCount: number;
  nextDueDate: string | null;
  overdueCount: number;
  status: PaymentPlanStatus;
  createdAt: string;
}

/** How a checkout finances what its tenders leave unpaid. Every field is optional; the server fills the tenant's defaults. */
export interface CreditTermsInput {
  /** 1–60, and no more than the tenant's `maxInstallments`. One means a single deferred balance. */
  installmentCount?: number;
  /** `YYYY-MM-DD` in the tenant's timezone, not in the past. Defaults to today plus `defaultCreditDays`. */
  firstDueDate?: string;
  interval?: 'MONTHLY' | 'WEEKLY' | 'DAYS';
  /** 1–365, read only when `interval` is DAYS. */
  intervalDays?: number;
  /** Required to exceed the customer's credit limit where the policy allows an override. */
  overrideReason?: string;
}

export interface PaymentPlanDetail extends PaymentPlanSummary {
  installments: InstallmentSummary[];
  cancelledReason: string | null;
  createdByName: string | null;
}

export interface SalesCreditNoteLineSummary {
  id: string;
  orderLineId: string;
  sku: string;
  productName: string;
  quantity: number;
  netMinor: number;
  taxMinor: number;
  grossMinor: number;
}

export interface SalesCreditNoteSummary {
  id: string;
  reference: string;
  status: CreditNoteStatus;
  kind: CreditNoteKind;
  orderId: string;
  orderReference: string;
  invoiceId: string | null;
  returnId: string | null;
  customerId: string | null;
  customerName: string | null;
  branchId: string;
  branchName: string;
  currencyCode: string;
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  reason: string | null;
  issuedAt: string;
  issuedByName: string | null;
}

export interface SalesCreditNoteDetail extends SalesCreditNoteSummary {
  lines: SalesCreditNoteLineSummary[];
  voidedReason: string | null;
}

/** One order's position, obligation and collection kept apart throughout. */
export interface OrderReceivable {
  orderId: string;
  orderReference: string;
  currencyCode: string;
  /** What was charged, before any credit note. */
  chargedMinor: number;
  creditedMinor: number;
  /** `charged − credited`. What the customer owes for goods. */
  netObligationMinor: number;
  paidMinor: number;
  refundedMinor: number;
  /** `paid − refunded`. Money actually in the till for this order. */
  netCollectedMinor: number;
  /** `max(0, obligation − collected)`. Never negative. */
  outstandingMinor: number;
  /** `max(0, collected − obligation)`. Money the shop is holding that is no longer owed. */
  refundableExcessMinor: number;
  planId: string | null;
}

export interface CustomerReceivable {
  customerId: string;
  customerName: string;
  currencyCode: string;
  netObligationMinor: number;
  netCollectedMinor: number;
  outstandingMinor: number;
  refundableExcessMinor: number;
  creditLimitMinor: number;
  /** `limit − outstanding`, floored at zero; null when some of the customer's debt lies in a branch the caller cannot reach, because a partial view would overstate it. */
  availableCreditMinor: number | null;
  overdueMinor: number;
  overdueCount: number;
  orders: OrderReceivable[];
}

export const AGEING_BUCKETS = ['CURRENT', 'D1_30', 'D31_60', 'D61_90', 'D90_PLUS'] as const;
export type AgeingBucket = (typeof AGEING_BUCKETS)[number];

export interface AgeingRow {
  customerId: string;
  customerName: string;
  currencyCode: string;
  buckets: Record<AgeingBucket, number>;
  totalMinor: number;
}

export interface AgeingReport {
  asOf: string;
  currencyCode: string;
  totals: Record<AgeingBucket, number>;
  totalMinor: number;
  rows: AgeingRow[];
}

/** The answer to "may this customer take on this much more?", before anything is written. */
export interface CreditLimitAssessment {
  allowed: boolean;
  requiresOverride: boolean;
  limitMinor: number;
  currentOutstandingMinor: number;
  newFinancedMinor: number;
  proposedExposureMinor: number;
  excessMinor: number;
  warning: string | null;
}

/** A row on the collections list: one instalment, with enough context to act on it. */
export interface InstallmentWorkItem {
  id: string;
  planId: string;
  planReference: string;
  orderId: string;
  orderReference: string;
  branchId: string;
  branchName: string;
  customerId: string;
  customerName: string;
  currencyCode: string;
  sequence: number;
  dueDate: string;
  amountMinor: number;
  outstandingMinor: number;
  state: InstallmentState;
  daysOverdue: number | null;
}

/** A customer who owes something, for the outstanding list. */
export interface OutstandingCustomer {
  customerId: string;
  customerName: string;
  currencyCode: string;
  outstandingMinor: number;
  overdueMinor: number;
  overdueCount: number;
  creditLimitMinor: number;
  /** Null when some of the customer's debt lies in a branch the caller cannot reach. */
  availableCreditMinor: number | null;
  oldestDueDate: string | null;
}

export const INSTALLMENT_WINDOWS = ['UPCOMING', 'OVERDUE'] as const;
export type InstallmentWindow = (typeof INSTALLMENT_WINDOWS)[number];
